import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import { Edit, Plus, Trash2, Star, Power } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_PACKAGE = {
  id: '',
  name_ar: '',
  name_en: '',
  price: 0,
  original_price: null,
  currency: 'usd',
  duration_days: 30,
  features_ar: [''],
  features_en: [''],
  consultations_included: 1,
  popular: false,
  enabled: true,
  sort_order: 99,
};

export const AdminPackagesView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create-new
  const [form, setForm] = useState(BLANK_PACKAGE);
  const [saving, setSaving] = useState(false);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/packages`, { withCredentials: true });
      setPackages(res.data || []);
    } catch (e) {
      console.error('Failed to load packages:', e);
      toast.error(t('فشل تحميل الباقات', 'Failed to load packages'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...BLANK_PACKAGE });
    setOpen(true);
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      ...BLANK_PACKAGE,
      ...pkg,
      features_ar: pkg.features_ar?.length ? [...pkg.features_ar] : [''],
      features_en: pkg.features_en?.length ? [...pkg.features_en] : [''],
    });
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setFeature = (lang, idx, val) => setForm((f) => {
    const next = [...(f[`features_${lang}`] || [])];
    next[idx] = val;
    return { ...f, [`features_${lang}`]: next };
  });

  const addFeature = (lang) => setForm((f) => ({
    ...f, [`features_${lang}`]: [...(f[`features_${lang}`] || []), '']
  }));

  const removeFeature = (lang, idx) => setForm((f) => {
    const next = [...(f[`features_${lang}`] || [])];
    next.splice(idx, 1);
    return { ...f, [`features_${lang}`]: next.length ? next : [''] };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const cleaned = {
      ...form,
      price: parseFloat(form.price) || 0,
      original_price: form.original_price === '' || form.original_price == null
        ? null : parseFloat(form.original_price),
      duration_days: parseInt(form.duration_days, 10) || 30,
      consultations_included: parseInt(form.consultations_included, 10) || 1,
      sort_order: parseInt(form.sort_order, 10) || 99,
      features_ar: (form.features_ar || []).map((s) => s.trim()).filter(Boolean),
      features_en: (form.features_en || []).map((s) => s.trim()).filter(Boolean),
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/packages/${editingId}`, cleaned, { withCredentials: true });
        toast.success(t('تم تحديث الباقة', 'Package updated'));
      } else {
        if (!cleaned.id) {
          toast.error(t('يجب إدخال معرف الباقة', 'Package id is required'));
          setSaving(false);
          return;
        }
        await axios.post(`${API}/admin/packages`, cleaned, { withCredentials: true });
        toast.success(t('تمت إضافة الباقة', 'Package created'));
      }
      setOpen(false);
      await fetchPackages();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`${t('خطأ', 'Error')}: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(t(
      `هل أنت متأكد من حذف الباقة "${pkg.name_ar || pkg.id}"؟`,
      `Delete package "${pkg.name_en || pkg.id}"?`
    ))) return;
    try {
      await axios.delete(`${API}/admin/packages/${pkg.id}`, { withCredentials: true });
      toast.success(t('تم الحذف', 'Deleted'));
      await fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const toggleEnabled = async (pkg) => {
    try {
      await axios.put(
        `${API}/admin/packages/${pkg.id}`,
        { enabled: !pkg.enabled },
        { withCredentials: true }
      );
      await fetchPackages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-packages-view" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#2c3e50]">
          {t('إدارة الباقات', 'Manage Packages')}
        </h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#1e88e5] hover:bg-[#1976d2]"
              onClick={startCreate}
              data-testid="add-package-button"
            >
              <Plus size={18} className={isRTL ? 'ml-2' : 'mr-2'} />
              {t('إضافة باقة', 'Add Package')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId
                  ? t('تعديل الباقة', 'Edit Package')
                  : t('إضافة باقة جديدة', 'Add New Package')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingId && (
                <div>
                  <Label htmlFor="pkg-id">{t('معرف الباقة (إنجليزي، بدون مسافات)', 'Package ID (slug)')}</Label>
                  <Input
                    id="pkg-id"
                    value={form.id}
                    onChange={(e) => setField('id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="e.g. premium_call"
                    required
                    data-testid="package-id-input"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>{t('الاسم بالعربية', 'Arabic Name')}</Label>
                  <Input value={form.name_ar} onChange={(e) => setField('name_ar', e.target.value)} required data-testid="package-name-ar" />
                </div>
                <div>
                  <Label>{t('الاسم بالإنجليزية', 'English Name')}</Label>
                  <Input value={form.name_en} onChange={(e) => setField('name_en', e.target.value)} required data-testid="package-name-en" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>{t('السعر', 'Price')}</Label>
                  <Input type="number" step="0.01" value={form.price} onChange={(e) => setField('price', e.target.value)} required data-testid="package-price" />
                </div>
                <div>
                  <Label>{t('السعر قبل الخصم', 'Original Price')}</Label>
                  <Input type="number" step="0.01" value={form.original_price ?? ''} onChange={(e) => setField('original_price', e.target.value)} placeholder="—" />
                </div>
                <div>
                  <Label>{t('عدد المكالمات', 'Calls Included')}</Label>
                  <Input type="number" value={form.consultations_included} onChange={(e) => setField('consultations_included', e.target.value)} />
                  <p className="text-[10px] text-gray-500 mt-1">{t('-1 = غير محدود', '-1 = unlimited')}</p>
                </div>
                <div>
                  <Label>{t('مدة الصلاحية (أيام)', 'Duration (days)')}</Label>
                  <Input type="number" value={form.duration_days} onChange={(e) => setField('duration_days', e.target.value)} />
                </div>
              </div>

              {/* Features AR */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t('المزايا (عربي)', 'Features (Arabic)')}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addFeature('ar')}>
                    <Plus size={14} /> {t('إضافة', 'Add')}
                  </Button>
                </div>
                <div className="space-y-2 mt-2">
                  {form.features_ar.map((f, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={f} onChange={(e) => setFeature('ar', idx, e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeFeature('ar', idx)}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features EN */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t('المزايا (إنجليزي)', 'Features (English)')}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => addFeature('en')}>
                    <Plus size={14} /> {t('إضافة', 'Add')}
                  </Button>
                </div>
                <div className="space-y-2 mt-2">
                  {form.features_en.map((f, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input value={f} onChange={(e) => setFeature('en', idx, e.target.value)} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeFeature('en', idx)}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2">
                  <Switch checked={!!form.popular} onCheckedChange={(v) => setField('popular', v)} />
                  <span className="text-sm">{t('الأكثر شعبية', 'Most popular badge')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={!!form.enabled} onCheckedChange={(v) => setField('enabled', v)} />
                  <span className="text-sm">{t('مفعّل', 'Enabled')}</span>
                </label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{t('ترتيب', 'Sort')}</Label>
                  <Input type="number" className="w-20" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t('إلغاء', 'Cancel')}
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#1e88e5] hover:bg-[#1976d2]" data-testid="save-package-btn">
                  {saving ? t('جارٍ الحفظ…', 'Saving…') : t('حفظ', 'Save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-gray-500">{t('جاري التحميل…', 'Loading…')}</p>
      ) : packages.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">{t('لا توجد باقات', 'No packages')}</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`p-5 relative ${pkg.popular ? 'border-2 border-[#1e88e5]' : ''} ${!pkg.enabled ? 'opacity-60' : ''}`}
              data-testid={`admin-package-card-${pkg.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs text-gray-500 font-mono">{pkg.id}</p>
                  <h3 className="font-bold text-[#2c3e50]">{language === 'ar' ? pkg.name_ar : pkg.name_en}</h3>
                  <p className="text-xs text-gray-500">{language === 'ar' ? pkg.name_en : pkg.name_ar}</p>
                </div>
                {pkg.popular && (
                  <span className="inline-flex items-center gap-1 text-xs bg-[#1e88e5] text-white px-2 py-0.5 rounded-full">
                    <Star size={12} fill="white" /> {t('الأكثر شعبية', 'Popular')}
                  </span>
                )}
              </div>

              <div className="mb-3">
                {pkg.original_price ? (
                  <span className="text-sm text-gray-400 line-through me-2">${pkg.original_price}</span>
                ) : null}
                <span className="text-2xl font-bold text-[#1e88e5]">${pkg.price}</span>
                <span className="text-gray-500 text-sm"> / {pkg.duration_days}d</span>
              </div>

              <ul className="space-y-1 text-sm text-gray-700 mb-3 list-disc ms-5">
                {(language === 'ar' ? pkg.features_ar : pkg.features_en)?.slice(0, 4).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>

              <div className="text-xs text-gray-500 mb-3">
                {t('عدد المكالمات:', 'Calls:')}{' '}
                <strong>{pkg.consultations_included === -1 ? t('غير محدود', 'Unlimited') : pkg.consultations_included}</strong>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => startEdit(pkg)} data-testid={`edit-package-${pkg.id}`}>
                  <Edit size={14} className={isRTL ? 'ml-1' : 'mr-1'} /> {t('تعديل', 'Edit')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleEnabled(pkg)}
                  data-testid={`toggle-package-${pkg.id}`}
                >
                  <Power size={14} className={isRTL ? 'ml-1' : 'mr-1'} />
                  {pkg.enabled ? t('تعطيل', 'Disable') : t('تفعيل', 'Enable')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(pkg)}
                  data-testid={`delete-package-${pkg.id}`}
                >
                  <Trash2 size={14} className={isRTL ? 'ml-1' : 'mr-1'} /> {t('حذف', 'Delete')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPackagesView;
